import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import { useQuery } from "../_app/lib/query";
import { AjaxResponse } from "../_app/api";
import {
  getBarById,
  getBarByIdCostCentre,
  getBars,
  getBarsByAssetId,
  getBarsForClis,
  getBarsForCostCentre,
  getCostCenterBarsByAssetId,
  updateBars,
  updateBarsForCostCentre,
} from "./api";
import { BarUpdateParams } from "./types";
import { useFeedbackAlerts } from "../_app/hooks";
import { genericError } from "../_app/utils/text";
import { UserType } from "../user-level/types";
import { useContextProvider } from "../context/providers/ContextProvider";

export function useBarsByAssetId(assetId: string, options = {}) {
  const { contextId, userType } = useContextProvider();

  return useQuery<any, AjaxResponse>({
    queryKey: ["barringDetails", { assetId }],

    queryFn: () => {
      if (userType === UserType.COST_CENTRE) {
        return getCostCenterBarsByAssetId(contextId, assetId);
      } else {
        return getBarsByAssetId(assetId);
      }
    },

    ...options,
  });
}

export function useBars(params: any = {}, options = {}) {
  const { contextId, userType } = useContextProvider();

  return useQuery<any, AjaxResponse>({
    queryKey: ["bars", params],

    queryFn: () => {
      if (userType === UserType.COST_CENTRE) {
        return getBarsForCostCentre(contextId, params);
      } else {
        return getBars(contextId, params);
      }
    },

    ...options,
  });
}

export function useSingleBar(barId: string, assetId: string, options = {}) {
  const { contextId, userType } = useContextProvider();

  const callback =
    userType === UserType.COST_CENTRE ? () => getBarByIdCostCentre(contextId, barId, assetId) : () => getBarById(barId, assetId);

  return useQuery<any, AjaxResponse>({
    queryKey: [barId, assetId],
    queryFn: callback,
    ...options,
  });
}

export function useBarsUpdate(options: UseMutationOptions<any, any, any> = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId, userType } = useContextProvider();

  const callback = userType === UserType.COST_CENTRE ? updateBarsForCostCentre : updateBars;

  return useMutation({
    mutationFn: (params: BarUpdateParams) => callback(contextId, params),
    ...options,

    onError: (updateError: any) => {
      setFeedbackAlertError(updateError?.data?.message || genericError());
    },
  });
}

export function useBarsForClis(clis: any[] = [], options = {}) {
  return useQuery<any, AjaxResponse>({
    queryKey: ["cliBars", clis],
    queryFn: () => getBarsForClis(clis),
    ...options,
  });
}
